import React, { ReactNode, ReactPortal, RefObject, SyntheticEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { SingleArrowUp } from '@r1-frontend/ui-react/components/svg/arrows';
import { Close } from '@r1-frontend/ui-react/components/svg/main';
import { createRootElement } from '@r1-frontend/shared/helpers/dom';

import * as S from './styled';

export type BottomStickyBlockProps = {
    children?: ReactNode,
    InnerComponent: () => JSX.Element,
    OuterComponent: () => JSX.Element,
    parent?: RefObject<HTMLElement>,
    onOpen?: () => void,
    onClose?: () => void,
}

const BottomStickyBlock = (
    {
        InnerComponent,
        OuterComponent,
        parent,
        onOpen = () => true,
        onClose = () => true,
    }: BottomStickyBlockProps,
): ReactPortal | null => {
    const [isShown, setIsShown] = useState(false);
    const [popupRoot, setPopupRoot] = useState<HTMLElement | null>(null);

    useEffect(() => {
        const root = parent ? parent.current : createRootElement('bottom-sticky');
        setPopupRoot(root);

        return () => {
            popupRoot && !parent && popupRoot.remove();
        };
    }, []);

    const onCloseHandler = (e: SyntheticEvent) => {
        e.stopPropagation();
        setIsShown(false);
        onClose();
    };

    const onOpenHandler = (e: SyntheticEvent) => {
        e.stopPropagation();
        setIsShown(true);
        onOpen();
    };

    return popupRoot
        ? createPortal(
            <S.Wrapper
                isShown={isShown}
            >
                {isShown
                    ? <>
                        <S.Overlay onClick={onCloseHandler} data-test='sticky-overlay'/>
                        <S.FullBody>
                            <S.Strip onClick={onCloseHandler}/>
                            <S.ActionButton onClick={onCloseHandler}><Close size={20}/></S.ActionButton>
                            <InnerComponent/>
                        </S.FullBody>
                    </>
                    : <S.ShortBody>
                        <S.Strip onClick={onOpenHandler}/>
                        <OuterComponent/>
                        <S.ActionButton data-test='sticky-button-expand' onClick={onOpenHandler}><SingleArrowUp size={20}/></S.ActionButton>
                    </S.ShortBody>
                }
            </S.Wrapper>,
            popupRoot,
        )
        : null;
};

BottomStickyBlock.displayName = 'BottomStickyBlock';

export default BottomStickyBlock;
